/**
 * Klient HTTP domu — wyłącznie zamrożone endpointy §18.
 * GET /api/houses, GET /api/houses/:id/enter, PUT /api/layout {locationType:'home'}.
 */
import {
  HouseEnterResponseSchema,
  HousesListResponseSchema,
  LayoutPutRequestSchema,
  LayoutPutResponseSchema,
  ResidencyRequestResponseSchema,
  type HouseEnterResponse,
  type HousesListResponse,
  type LayoutPutResponse,
  type PlacedBlock,
} from '@pieskowo/shared';
import { apiBase, authHeaders } from '../../core/apiBase.js';
import { SaveLocal } from '../../core/SaveLocal.js';
import { newIdempotencyKey } from '../../avatar/api.js';
import {
  LOCAL_HOME_LAYOUT_KEY,
  LOCAL_HOME_VERSION_KEY,
  LOCAL_OWNED_BLOCKS_KEY,
  defaultOwnedHomeBlocks,
  normalizeLayout,
} from './layoutModel.js';

export type HouseLoadResult = {
  houseId: string | null;
  layout: PlacedBlock[];
  layoutVersion: number;
  isOwner: boolean;
  source: 'server' | 'local';
};

export async function listHouses(page = 0): Promise<HousesListResponse | null> {
  try {
    const res = await fetch(`${apiBase()}/api/houses?page=${page}`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const parsed = HousesListResponseSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export type ResidencyRequestResult =
  | { ok: true; status: 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked' }
  | { ok: false; code: string };

/**
 * POST /api/houses/:id/residency-request — prośba „zamieszkaj razem" (§9.2).
 * Komunikat w 100% predefiniowany (brak pól treści). Idempotencyjny klucz.
 */
export async function requestResidency(
  houseId: string,
): Promise<ResidencyRequestResult> {
  try {
    const res = await fetch(
      `${apiBase()}/api/houses/${encodeURIComponent(houseId)}/residency-request`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ idempotencyKey: newIdempotencyKey() }),
      },
    );
    if (!res.ok) {
      const code =
        res.status === 429
          ? 'RATE_LIMIT'
          : res.status === 401
            ? 'UNAUTHORIZED'
            : 'ERROR';
      return { ok: false, code };
    }
    const json: unknown = await res.json();
    const parsed = ResidencyRequestResponseSchema.safeParse(json);
    if (!parsed.success) return { ok: false, code: 'BAD_RESPONSE' };
    return { ok: true, status: parsed.data.status };
  } catch {
    return { ok: false, code: 'NETWORK' };
  }
}

export async function enterHouse(houseId: string): Promise<HouseEnterResponse | null> {
  try {
    const res = await fetch(`${apiBase()}/api/houses/${encodeURIComponent(houseId)}/enter`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const parsed = HouseEnterResponseSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export type PutLayoutResult =
  | {
      ok: true;
      version: number;
      /** 0 gdy brak nagrody (już dziś / bez istotnej zmiany / offline). */
      rewardZl: number;
      newBalanceZl: number | null;
    }
  | { ok: false; code: string; status: number };

function readRewardFields(json: unknown): {
  rewardZl: number;
  newBalanceZl: number | null;
} {
  if (!json || typeof json !== 'object') {
    return { rewardZl: 0, newBalanceZl: null };
  }
  const o = json as Record<string, unknown>;
  const rewardZl =
    typeof o.rewardZl === 'number' && Number.isFinite(o.rewardZl)
      ? Math.max(0, Math.floor(o.rewardZl))
      : 0;
  const newBalanceZl =
    typeof o.newBalanceZl === 'number' && Number.isFinite(o.newBalanceZl)
      ? Math.max(0, Math.floor(o.newBalanceZl))
      : null;
  return { rewardZl, newBalanceZl };
}

export async function putHomeLayout(
  layout: PlacedBlock[],
  version: number,
): Promise<PutLayoutResult> {
  const body = {
    locationType: 'home' as const,
    layout,
    version,
  };
  const req = LayoutPutRequestSchema.safeParse(body);
  if (!req.success) return { ok: false, code: 'VALIDATION', status: 400 };

  try {
    const res = await fetch(`${apiBase()}/api/layout`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(req.data),
    });
    if (!res.ok) {
      let code = 'ERROR';
      try {
        const errJson = (await res.json()) as { error?: { code?: string } };
        code = errJson?.error?.code ?? code;
      } catch {
        /* ignore */
      }
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const parsed = LayoutPutResponseSchema.safeParse(json);
    if (!parsed.success) return { ok: false, code: 'VALIDATION', status: 500 };
    const out: LayoutPutResponse = parsed.data;
    const rewards = readRewardFields(json);
    // lokalny mirror po sukcesie
    saveLocalHome(layout, out.version);
    return {
      ok: true,
      version: out.version,
      rewardZl: rewards.rewardZl,
      newBalanceZl: rewards.newBalanceZl,
    };
  } catch {
    return { ok: false, code: 'NETWORK', status: 0 };
  }
}

export function loadLocalHome(): { layout: PlacedBlock[]; layoutVersion: number } {
  const layout = normalizeLayout(
    SaveLocal.getJson<unknown>(LOCAL_HOME_LAYOUT_KEY, []),
  );
  const layoutVersion = SaveLocal.getJson<number>(LOCAL_HOME_VERSION_KEY, 0);
  return { layout, layoutVersion: Number.isInteger(layoutVersion) ? layoutVersion : 0 };
}

export function saveLocalHome(layout: PlacedBlock[], version: number): void {
  SaveLocal.setJson(LOCAL_HOME_LAYOUT_KEY, layout);
  SaveLocal.setJson(LOCAL_HOME_VERSION_KEY, version);
}

export function loadOwnedBlocks(): string[] {
  const raw = SaveLocal.getJson<unknown>(LOCAL_OWNED_BLOCKS_KEY, null);
  if (Array.isArray(raw) && raw.every((x) => typeof x === 'string') && raw.length > 0) {
    return raw as string[];
  }
  return defaultOwnedHomeBlocks();
}

export function saveOwnedBlocks(ids: string[]): void {
  SaveLocal.setJson(LOCAL_OWNED_BLOCKS_KEY, ids);
}

/**
 * Ładuje layout domu: własny (badge owned) lub podany houseId.
 * Offline → lokalny draft / pusty.
 */
export async function loadHouseLayout(opts?: {
  houseId?: string;
}): Promise<HouseLoadResult> {
  const token = SaveLocal.getToken();
  if (!token) {
    const local = loadLocalHome();
    return {
      houseId: null,
      layout: local.layout,
      layoutVersion: local.layoutVersion,
      isOwner: true,
      source: 'local',
    };
  }

  let houseId = opts?.houseId ?? null;
  let isOwner = true;

  if (!houseId) {
    const list = await listHouses(0);
    const own = list?.houses.find((h) => h.badge === 'owned');
    if (own) {
      houseId = own.id;
      isOwner = true;
    } else if (list?.houses[0]) {
      // fallback: pierwszy dostępny (member) — tylko podgląd
      const first = list.houses.find((h) => h.badge === 'member' || h.badge === 'owned');
      if (first) {
        houseId = first.id;
        isOwner = first.badge === 'owned';
      }
    }
  } else {
    const list = await listHouses(0);
    const hit = list?.houses.find((h) => h.id === houseId);
    // brak listy (offline) → zakładamy właściciela (budowanie lokalne)
    isOwner = hit ? hit.badge === 'owned' : true;
  }

  if (houseId) {
    const entered = await enterHouse(houseId);
    if (entered) {
      const layout = normalizeLayout(entered.layout);
      if (isOwner) saveLocalHome(layout, entered.layoutVersion);
      return {
        houseId,
        layout,
        layoutVersion: entered.layoutVersion,
        isOwner,
        source: 'server',
      };
    }
  }

  const local = loadLocalHome();
  return {
    houseId,
    layout: local.layout,
    layoutVersion: local.layoutVersion,
    isOwner,
    source: 'local',
  };
}
